import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Lock, RefreshCw, FileEdit, ChevronDown, ChevronUp, PenLine, Check,
  Phone, Layers, CalendarClock, MapPin, Tv, Hash, CalendarDays, Bell, BellOff,
} from 'lucide-react';
import { customerAPI, catvAPI, authAPI } from '../../services/api';
import { pushSupported, getExistingSubscription, enablePush, disablePush } from '../../utils/webPush';
import CustomerAppInstall from '../../components/customer/CustomerAppInstall';
import SignaturePad from '../../components/agent/SignaturePad';
import SearchableSelect from '../../components/common/SearchableSelect';
import { getBillingAddress } from '../../utils/billingAddress';
import { customerTypeLabel } from '../../utils/customerTypeLabel';

const DISCONNECTION_REASONS = [
  'Temporary',
  'Switched to Jio/Airtel/GTPL Broadband',
  'Exams',
  'Vacation',
];

export default function CustomerProfile() {
  const [customer, setCustomer]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [section, setSection]     = useState(null); // 'password' | 'changes'
  const [saving, setSaving]       = useState(false);

  const [passForm, setPass]       = useState({ currentPassword: '', newPassword: '', confirm: '' });

  const [pushOk, setPushOk]       = useState(pushSupported());
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy]   = useState(false);

  const [packages, setPackages]   = useState([]);
  const [reqType, setReqType]     = useState('plan_change');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [disconnectReason, setDisconnectReason] = useState('');
  const [notes, setNotes]         = useState('');
  const [signature, setSignature] = useState(null);
  const [signOpen, setSignOpen]   = useState(false);

  useEffect(() => {
    customerAPI.getMe().then(r => {
      setCustomer(r.data.data);
    }).catch(() => toast.error('Could not load profile'))
      .finally(() => setLoading(false));
    catvAPI.getPackages().then(r => setPackages(r.data.data || [])).catch(() => {});
    if (pushSupported()) {
      getExistingSubscription().then(sub => setPushEnabled(!!sub)).catch(() => {});
    }
  }, []);

  const toggle = (s) => setSection(prev => prev === s ? null : s);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushEnabled) {
        await disablePush();
        setPushEnabled(false);
        toast.success('Push notifications turned off');
      } else {
        await enablePush();
        setPushEnabled(true);
        toast.success('Push notifications enabled');
      }
    } catch (err) {
      toast.error(err.message || 'Could not update push notifications');
    } finally {
      setPushBusy(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (passForm.newPassword !== passForm.confirm) { toast.error('New passwords do not match'); return; }
    if (passForm.newPassword.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    setSaving(true);
    try {
      await authAPI.changePassword({ currentPassword: passForm.currentPassword, newPassword: passForm.newPassword });
      toast.success('Password changed successfully');
      setPass({ currentPassword: '', newPassword: '', confirm: '' });
      setSection(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Password change failed');
    } finally { setSaving(false); }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    const value = reqType === 'plan_change' ? selectedPackage : disconnectReason;
    if (!value) { toast.error(reqType === 'plan_change' ? 'Select a plan' : 'Select a reason'); return; }
    if (!signature) { toast.error('Please sign to confirm this request'); return; }
    setSaving(true);
    try {
      const res = await customerAPI.submitServiceRequest({ type: reqType, value, notes, signature });
      toast.success(res.data.message || `Request submitted — Ticket: ${res.data.data.ticketNumber}`);
      setSelectedPackage(''); setDisconnectReason(''); setNotes(''); setSignature(null);
      setSection(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Request failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>;
  if (!customer) return null;

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

  // A CATV connection and a Broadband connection are sometimes registered as two
  // separate accounts sharing one login (linked via the admin's "Link Account" panel) —
  // combine them here so Account Information reflects both, same as the Dashboard.
  const linked = customer.linkedAccount || null;
  const bbSource   = customer.customerType !== 'catv' ? customer : linked;
  const catvSource = customer.customerType !== 'broadband' ? customer : linked;
  const hasBB   = (bbSource?.customerType === 'broadband' || bbSource?.customerType === 'both') && !!bbSource?.broadband;
  const hasCatv = (catvSource?.customerType === 'catv' || catvSource?.customerType === 'both') && !!catvSource?.catv;

  const billingCycleLabel = (source) => {
    const b = source?.billing;
    if (!b) return null;
    return b.durationType === 'dtd' ? `${b.durationDays || 30} Days` : 'Monthly (Auto-Renew)';
  };

  const addrBlock = getBillingAddress(customer);
  const hasCatvPlan = !!(customer.catv?.package || customer.catv?.expiryDate || customer.catv?.setTopBoxSerial);

  const infoRows = [
    { icon: Phone, label: 'Mobile', value: customer.mobile, sub: customer.alternateMobile ? `${customer.alternateMobile} (alt)` : null },
    { icon: Layers, label: 'Service Type', value: hasBB && hasCatv ? 'CableTV + Broadband' : customerTypeLabel(customer.customerType) },
    hasCatv && billingCycleLabel(catvSource) ? { icon: CalendarClock, label: hasBB ? 'CableTV Billing' : 'Billing Cycle', value: billingCycleLabel(catvSource) } : null,
    // Broadband billing is its own separate `billing` subdocument on a linked account —
    // for a CableTV customer it's frequently just the unset schema default ("Monthly
    // Auto-Renew"), not a real cycle, so it's only shown for broadband-only customers,
    // where it's the customer's actual (and only) billing cycle.
    !hasCatv && hasBB && billingCycleLabel(bbSource) ? { icon: CalendarClock, label: 'Billing Cycle', value: billingCycleLabel(bbSource) } : null,
  ].filter(Boolean);

  return (
    <div style={{ maxWidth: 520, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 20, fontSize: '1.15rem', fontWeight: 700 }}>My Account</h2>

      {/* Account Information — avatar/name header + a single details grid (no fields repeated) */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{
              width: 50, height: 50, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, var(--primary) 0%, #1e3a8a 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '1.25rem',
            }}>
              {customer.name?.[0]?.toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: '1.02rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.name}</div>
              {customer.customerId && (
                <div className="font-mono" style={{ color: 'var(--text-muted)', fontSize: '.78rem', marginTop: 1 }}>ID: {customer.customerId}</div>
              )}
            </div>
          </div>
          <span className={`badge badge-${customer.status === 'active' || !customer.status ? 'success' : customer.status === 'suspended' ? 'warning' : 'danger'}`} style={{ textTransform: 'capitalize', flexShrink: 0 }}>
            {customer.status || 'active'}
          </span>
        </div>

        <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
          Account Details
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 }}>
          {infoRows.map(({ icon: Icon, label, value, sub, capitalize }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{
                width: 30, height: 30, borderRadius: 9, background: 'var(--primary-light)', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
              }}>
                <Icon size={14} color="var(--primary)" />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={infoLabel}>{label}</div>
                <div style={{ fontWeight: 700, fontSize: '.88rem', textTransform: capitalize ? 'capitalize' : 'none' }}>{value}</div>
                {sub && <div style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>{sub}</div>}
              </div>
            </div>
          ))}
        </div>

        {addrBlock.line1 && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <div style={{
              width: 30, height: 30, borderRadius: 9, background: 'var(--primary-light)', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
            }}>
              <MapPin size={14} color="var(--primary)" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={infoLabel}>Billing Address</div>
              <div style={{ fontWeight: 600, fontSize: '.88rem' }}>
                {[addrBlock.line1, addrBlock.area, addrBlock.city, addrBlock.pincode].filter(Boolean).join(', ')}
              </div>
            </div>
          </div>
        )}

        {hasCatvPlan && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
              CableTV Plan
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{
                width: 30, height: 30, borderRadius: 9, background: '#dbeafe', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
              }}>
                <Tv size={14} color="var(--primary)" />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                {customer.catv?.package && (
                  <div style={{ fontWeight: 700, fontSize: '.9rem' }}>
                    {customer.catv.package.displayName} <span style={{ fontWeight: 600, color: 'var(--primary)' }}>— ₹{customer.catv.package.price}/mo</span>
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6 }}>
                  {customer.catv?.expiryDate && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.78rem', color: 'var(--text-muted)' }}>
                      <CalendarDays size={12} /> Expires {fmtDate(customer.catv.expiryDate)}
                    </span>
                  )}
                  {customer.catv?.setTopBoxSerial && (
                    <span className="font-mono" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.78rem', color: 'var(--text-muted)' }}>
                      <Hash size={12} /> {customer.catv.setTopBoxSerial}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Need to change details */}
      <div style={{ ...card, marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <FileEdit size={16} style={{ marginTop: 2, flexShrink: 0, color: 'var(--primary)' }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: '.95rem', marginBottom: 4 }}>Need to change details?</div>
            <p style={{ fontSize: '.85rem', color: '#555', margin: 0 }}>
              Name, address and mobile number can only be updated by our team — please contact your admin to request a change.
            </p>
            <p style={{ fontSize: '.78rem', color: '#888', marginTop: 8, marginBottom: 0 }}>
              Please bring: <strong>Aadhar Card</strong>, <strong>Light Bill</strong> and a <strong>Photo</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div style={{ ...card, marginTop: 14 }}>
        <button onClick={() => toggle('password')} style={sectionBtn}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Lock size={16} /> Change Password</span>
          {section === 'password' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {section === 'password' && (
          <form onSubmit={savePassword} style={{ marginTop: 14 }}>
            <Field label="Current Password" value={passForm.currentPassword} onChange={v => setPass(f => ({ ...f, currentPassword: v }))} type="password" hint="Default: last 4 digits of your registered mobile number" />
            <Field label="New Password" value={passForm.newPassword} onChange={v => setPass(f => ({ ...f, newPassword: v }))} type="password" />
            <Field label="Confirm New Password" value={passForm.confirm} onChange={v => setPass(f => ({ ...f, confirm: v }))} type="password" />
            <button type="submit" disabled={saving} style={submitBtn}><Lock size={15} /> {saving ? 'Updating...' : 'Change Password'}</button>
          </form>
        )}
      </div>

      {/* Push Notifications */}
      <div style={{ ...card, marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            {pushEnabled ? <Bell size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} /> : <BellOff size={16} style={{ color: '#888', flexShrink: 0 }} />}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '.95rem' }}>Push Notifications</div>
              <div style={{ fontSize: '.78rem', color: '#888', marginTop: 2 }}>
                {!pushOk ? 'Not supported on this browser' : pushEnabled ? 'Enabled on this device — offers & updates' : 'Get offers & updates on this device'}
              </div>
            </div>
          </div>
          {pushOk && (
            <button
              onClick={togglePush}
              disabled={pushBusy}
              aria-label="Toggle push notifications"
              style={{
                flexShrink: 0, width: 46, height: 26, borderRadius: 13, border: 'none', cursor: pushBusy ? 'default' : 'pointer',
                background: pushEnabled ? 'var(--primary)' : '#d1d5db', position: 'relative', padding: 0,
                opacity: pushBusy ? 0.6 : 1, transition: 'background .15s',
              }}
            >
              <span style={{
                position: 'absolute', top: 3, left: pushEnabled ? 23 : 3, width: 20, height: 20, borderRadius: '50%',
                background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.3)', transition: 'left .15s',
              }} />
            </button>
          )}
        </div>
      </div>

      <CustomerAppInstall />

      {/* Need to Request Changes */}
      <div style={{ ...card, marginTop: 14 }}>
        <button onClick={() => toggle('changes')} style={sectionBtn}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><RefreshCw size={16} /> Need to Request Changes</span>
          {section === 'changes' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {section === 'changes' && (
          <form onSubmit={submitRequest} style={{ marginTop: 14 }}>
            <div style={{ padding: '8px 12px', background: '#f0f9ff', borderRadius: 8, fontSize: '.83rem', color: '#0369a1', marginBottom: 12 }}>
              Our team will contact you within 24 hours to process this request.
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={selectLabel}>Request Type</label>
              <select value={reqType} style={selectStyle}
                onChange={e => { setReqType(e.target.value); setSelectedPackage(''); setDisconnectReason(''); }}>
                <option value="plan_change">Plan Change</option>
                <option value="disconnection">Disconnection</option>
              </select>
            </div>

            {reqType === 'plan_change' ? (
              <div style={{ marginBottom: 10 }}>
                <label style={selectLabel}>Select New Plan</label>
                <SearchableSelect
                  value={selectedPackage}
                  onChange={setSelectedPackage}
                  placeholder="Search package…"
                  options={packages.map(p => ({ value: p._id, label: `${p.displayName || p.name} — ₹${p.price}/mo` }))}
                />
              </div>
            ) : (
              <div style={{ marginBottom: 10 }}>
                <label style={selectLabel}>Reason for Disconnection</label>
                <select value={disconnectReason} onChange={e => setDisconnectReason(e.target.value)} style={selectStyle}>
                  <option value="">— Select a reason —</option>
                  {DISCONNECTION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}

            <div style={{ marginBottom: 10 }}>
              <label style={selectLabel}>Additional Notes (optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                rows={3} placeholder="Any specific requirements..."
                style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: '.9rem', boxSizing: 'border-box', resize: 'vertical' }} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={selectLabel}>Signature (confirms this request)</label>
              {signature ? (
                <div style={{ border: '1.5px solid #d1d5db', borderRadius: 8, padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <img src={signature} alt="signature" style={{ height: 44, objectFit: 'contain' }} />
                  <button type="button" onClick={() => setSignOpen(true)}
                    style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, fontSize: '.82rem', cursor: 'pointer' }}>
                    Re-sign
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setSignOpen(true)} style={signBtn}>
                  <PenLine size={15} /> Tap to Sign
                </button>
              )}
            </div>

            <button type="submit" disabled={saving} style={submitBtn}><Check size={15} /> {saving ? 'Submitting...' : 'Submit Request'}</button>
          </form>
        )}
      </div>

      {signOpen && (
        <SignaturePad value={signature} onSave={setSignature} onClose={() => setSignOpen(false)} />
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, hint }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: '.85rem', color: '#555', display: 'block', marginBottom: 4 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: '.9rem', boxSizing: 'border-box' }} />
      {hint && <p style={{ fontSize: '.75rem', color: '#888', margin: '3px 0 0' }}>{hint}</p>}
    </div>
  );
}

const card      = { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 8px rgba(0,0,0,.08)' };
const sectionBtn = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '.95rem', padding: 0 };
const submitBtn  = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '11px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', marginTop: 4 };
const infoLabel  = { fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: 2 };
const selectLabel = { fontSize: '.85rem', color: '#555', display: 'block', marginBottom: 4 };
const selectStyle = { width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: '.9rem', boxSizing: 'border-box', background: '#fff' };
const signBtn = {
  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  padding: '12px', border: '1.5px dashed #d1d5db', borderRadius: 8, background: '#f9fafb',
  color: 'var(--primary)', fontWeight: 600, fontSize: '.88rem', cursor: 'pointer',
};
